import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { BRAND } from "@/lib/brand";

export default function PrintPO() {
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/requirements", { params: { status: "requirement" } });
        setItems(data.items);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const grandTotal = items.reduce((sum, r) => {
    const p = r.product_snapshot || {};
    const cost = Number(p.last_purchase_cost || p.purchase_price || 0);
    return sum + cost * (r.quantity || 0);
  }, 0);

  return (
    <div className="min-h-screen bg-white print-po">
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
        .print-po table { border-collapse: collapse; width: 100%; font-size: 12px; }
        .print-po th, .print-po td { border: 1px solid #ddd; padding: 6px 8px; vertical-align: top; }
        .print-po th { background: #F5F4F0; text-align: left; text-transform: uppercase; font-size: 10px; letter-spacing: .04em; color: #4A4543; }
        .print-po .num { text-align: right; font-variant-numeric: tabular-nums; }
        .print-po .p-urgent { color: #991B1B; font-weight: 600; }
        .print-po .p-high { color: #9A3412; font-weight: 600; }
        .print-po .p-normal { color: #065F46; font-weight: 600; }
        .print-po .p-low { color: #374151; }
      `}</style>

      <div className="max-w-[1000px] mx-auto p-8">
        <div className="flex items-start justify-between mb-6 pb-6 border-b">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Purchase Order</div>
            <h1 className="font-heading text-3xl">{BRAND.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Generated on {formatDateTime(new Date().toISOString())}
            </p>
          </div>
          <div className="no-print flex gap-2">
            <Button variant="outline" onClick={() => window.close()} data-testid="print-close-btn">
              Close
            </Button>
            <Button onClick={() => window.print()} style={{ backgroundColor: "#9E6D5B" }} data-testid="print-trigger-btn">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </div>
        </div>

        {!ready ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground">No pending requirements to print.</div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>#</th>
                  <th>Product</th>
                  <th>Brand</th>
                  <th>Group</th>
                  <th>Manual BC</th>
                  <th>Auto BC</th>
                  <th className="num">Qty</th>
                  <th>Priority</th>
                  <th className="num">Last Cost</th>
                  <th className="num">Est. Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, idx) => {
                  const p = r.product_snapshot || {};
                  const cost = Number(p.last_purchase_cost || p.purchase_price || 0);
                  const total = cost * (r.quantity || 0);
                  return (
                    <tr key={r.id}>
                      <td>{idx + 1}</td>
                      <td>{p.product}</td>
                      <td>{p.brand_name}</td>
                      <td>{p.group_name}</td>
                      <td>{p.manual_barcode}</td>
                      <td>{p.auto_barcode}</td>
                      <td className="num">{r.quantity}</td>
                      <td className={`p-${r.priority}`}>{(r.priority || "").toUpperCase()}</td>
                      <td className="num">₹{cost.toFixed(2)}</td>
                      <td className="num">₹{total.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={8} style={{ textAlign: "right", fontWeight: 600 }}>
                    Grand Total
                  </td>
                  <td></td>
                  <td className="num" style={{ fontWeight: 700 }}>
                    ₹{grandTotal.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>

            <div className="mt-10 grid grid-cols-2 gap-10 text-xs">
              <div>
                <div className="uppercase tracking-widest text-muted-foreground mb-6">Prepared by</div>
                <div className="border-t pt-2">Signature / Name</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-muted-foreground mb-6">Approved by</div>
                <div className="border-t pt-2">Signature / Name</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
